/**
 * service接口配置
 */
import type { ApiEndpoint } from '../types';
import { API_CATEGORIES } from '../constants';

export const serviceEndpoints: ApiEndpoint[] = [
  {
    id: 'service-space-list',
    method: 'GET',
    path: '/api/service/spaces',
    name: '获取空间列表（服务接口）',
    description: '供其他服务调用的空间列表查询接口，使用服务token验证',
    category: API_CATEGORIES.SERVICE,
    queryParams: [
      { name: 'page', type: 'number', required: false, description: '页码', example: '1' },
      { name: 'limit', type: 'number', required: false, description: '每页条数', example: '10' },
      { name: 'type', type: 'string', required: false, description: '空间类型', example: 'BUILDING' },
      { name: 'status', type: 'string', required: false, description: '空间状态', example: 'ACTIVE' },
      { name: 'parentId', type: 'string', required: false, description: '父空间ID' },
      { name: 'search', type: 'string', required: false, description: '搜索关键词' },
      { name: 'tags', type: 'string|array', required: false, description: '标签ID或标签ID数组' }
    ],
    responseFields: [
      { name: 'success', type: 'boolean', description: '请求是否成功' },
      { name: 'data', type: 'array', description: '空间列表' },
      { name: 'pagination.total', type: 'number', description: '总记录数' },
      { name: 'pagination.page', type: 'number', description: '当前页码' },
      { name: 'pagination.limit', type: 'number', description: '每页条数' }
    ],
    requiredPermission: 'msspace:space:view (服务token权限)',
    responseExample: JSON.stringify({
      success: true,
      data: [
        {
          id: "55a8b310-f590-4671-8a78-db5a9de76e3c",
          name: "北京三元桥燕莎店",
          type: "BUILDING",
          spacePath: "北京三元桥燕莎店",
          parentId: null,
          description: "",
          status: "ACTIVE",
          createdAt: "2025-10-07T11:46:16.822Z",
          updatedAt: "2025-11-19T06:32:03.007Z",
          tags: []
        }
      ],
      pagination: {
        total: 10,
        page: 1,
        limit: 10
      },
      message: "查询成功"
    }, null, 2)
  },
  {
    id: 'service-space-tree',
    method: 'GET',
    path: '/api/service/spaces/tree',
    name: '获取空间树（服务接口）',
    description: '供其他服务调用的空间树查询接口，使用服务token验证',
    category: API_CATEGORIES.SERVICE,
    queryParams: [
      { name: 'rootId', type: 'string', required: false, description: '根节点ID（可选，不传则返回所有根节点）' }
    ],
    responseFields: [
      { name: 'success', type: 'boolean', description: '请求是否成功' },
      { name: 'data', type: 'array', description: '空间树结构' }
    ],
    requiredPermission: 'msspace:space:view (服务token权限)',
    responseExample: JSON.stringify({
      success: true,
      data: [
        {
          id: "55a8b310-f590-4671-8a78-db5a9de76e3c",
          name: "北京三元桥燕莎店",
          type: "BUILDING",
          spacePath: "北京三元桥燕莎店",
          parentId: null,
          tags: [],
          children: [],
          _count: {
            children: 0
          }
        }
      ],
      message: "空间树查询成功"
    }, null, 2)
  },
  {
    id: 'service-space-buildings',
    method: 'GET',
    path: '/api/service/spaces/buildings',
    name: '获取所有建筑节点（服务接口）',
    description: '供其他服务调用，获取所有建筑类型的空间节点，使用服务token验证',
    category: API_CATEGORIES.SERVICE,
    responseFields: [
      { name: 'success', type: 'boolean', description: '请求是否成功' },
      { name: 'data', type: 'array', description: '建筑节点列表' }
    ],
    requiredPermission: 'msspace:space:view (服务token权限)',
    responseExample: JSON.stringify({
      success: true,
      data: [
        {
          id: "55a8b310-f590-4671-8a78-db5a9de76e3c",
          name: "北京三元桥燕莎店",
          type: "BUILDING",
          spacePath: "北京三元桥燕莎店",
          status: "ACTIVE",
          tags: [],
          _count: {
            children: 5,
            coordinates: 1,
            markers: 10
          }
        }
      ],
      message: "建筑节点查询成功"
    }, null, 2)
  },
  {
    id: 'service-space-detail',
    method: 'GET',
    path: '/api/service/spaces/:id',
    name: '获取空间详情（服务接口）',
    description: '供其他服务调用，根据ID获取空间详细信息，使用服务token验证',
    category: API_CATEGORIES.SERVICE,
    pathParams: [
      { name: 'id', type: 'string', required: true, description: '空间ID（UUID）' }
    ],
    responseFields: [
      { name: 'success', type: 'boolean', description: '请求是否成功' },
      { name: 'data', type: 'object', description: '空间详细信息' }
    ],
    requiredPermission: 'msspace:space:view (服务token权限)',
    responseExample: JSON.stringify({
      success: true,
      data: {
        id: "55a8b310-f590-4671-8a78-db5a9de76e3c",
        name: "北京三元桥燕莎店",
        type: "BUILDING",
        spacePath: "北京三元桥燕莎店",
        parentId: null,
        description: "",
        status: "ACTIVE",
        createdAt: "2025-10-07T11:46:16.822Z",
        updatedAt: "2025-11-19T06:32:03.007Z",
        tags: []
      },
      message: "查询成功"
    }, null, 2)
  },
  {
    id: 'service-space-details',
    method: 'GET',
    path: '/api/service/spaces/:id/details',
    name: '获取空间节点详情（服务接口）',
    description: '供其他服务调用，获取空间节点的详细信息，包括关联数据，使用服务token验证',
    category: API_CATEGORIES.SERVICE,
    pathParams: [
      { name: 'id', type: 'string', required: true, description: '空间ID（UUID）' }
    ],
    responseFields: [
      { name: 'success', type: 'boolean', description: '请求是否成功' },
      { name: 'data', type: 'object', description: '空间节点详情（包含子节点、坐标、标记等）' }
    ],
    requiredPermission: 'msspace:space:view (服务token权限)',
    responseExample: JSON.stringify({
      success: true,
      data: {
        id: "55a8b310-f590-4671-8a78-db5a9de76e3c",
        name: "北京三元桥燕莎店",
        type: "BUILDING",
        tags: [],
        parent: null,
        children: [],
        coordinates: [],
        displays: [],
        markers: [],
        drawings: [],
        _count: {
          children: 0,
          coordinates: 0,
          markers: 0
        }
      },
      message: "节点详情查询成功"
    }, null, 2)
  },
  {
    id: 'service-space-children',
    method: 'GET',
    path: '/api/service/spaces/:id/children',
    name: '获取子空间（服务接口）',
    description: '供其他服务调用，获取指定空间的子空间列表，使用服务token验证',
    category: API_CATEGORIES.SERVICE,
    pathParams: [
      { name: 'id', type: 'string', required: true, description: '空间ID（UUID）' }
    ],
    queryParams: [
      { name: 'page', type: 'number', required: false, description: '页码' },
      { name: 'limit', type: 'number', required: false, description: '每页条数' },
      { name: 'type', type: 'string', required: false, description: '空间类型' },
      { name: 'status', type: 'string', required: false, description: '空间状态' },
      { name: 'search', type: 'string', required: false, description: '搜索关键词' },
      { name: 'tags', type: 'string|array', required: false, description: '标签ID或标签ID数组' }
    ],
    responseFields: [
      { name: 'success', type: 'boolean', description: '请求是否成功' },
      { name: 'data', type: 'array', description: '子空间列表' },
      { name: 'pagination', type: 'object', description: '分页信息' }
    ],
    requiredPermission: 'msspace:space:view (服务token权限)',
    responseExample: JSON.stringify({
      success: true,
      data: [],
      pagination: {
        total: 0,
        page: 1,
        limit: 10
      },
      message: "子空间查询成功"
    }, null, 2)
  },
  {
    id: 'service-space-subordinates',
    method: 'GET',
    path: '/api/service/spaces/:id/subordinates',
    name: '获取空间及下属（服务接口）',
    description: '供其他服务调用，获取空间及其所有递归下属空间（树结构），使用服务token验证',
    category: API_CATEGORIES.SERVICE,
    pathParams: [
      { name: 'id', type: 'string', required: true, description: '空间ID（UUID）' }
    ],
    responseFields: [
      { name: 'success', type: 'boolean', description: '请求是否成功' },
      { name: 'data.node', type: 'object', description: '当前节点信息' },
      { name: 'data.subordinates', type: 'array', description: '所有下属节点（递归）' }
    ],
    requiredPermission: 'msspace:space:view (服务token权限)',
    responseExample: JSON.stringify({
      success: true,
      data: {
        node: {
          id: "55a8b310-f590-4671-8a78-db5a9de76e3c",
          name: "北京三元桥燕莎店",
          type: "BUILDING",
          tags: [],
          _count: {
            children: 2,
            coordinates: 1,
            markers: 10
          }
        },
        subordinates: [
          {
            id: "child-id-1",
            name: "一层",
            type: "AREA",
            subordinates: []
          }
        ]
      },
      message: "节点及下属节点查询成功"
    }, null, 2)
  },
  {
    id: 'service-space-hierarchy',
    method: 'GET',
    path: '/api/service/spaces/:id/hierarchy',
    name: '获取空间层级（服务接口）',
    description: '供其他服务调用，获取空间的完整层级路径（从根节点到当前节点），使用服务token验证',
    category: API_CATEGORIES.SERVICE,
    pathParams: [
      { name: 'id', type: 'string', required: true, description: '空间ID（UUID）' }
    ],
    responseFields: [
      { name: 'success', type: 'boolean', description: '请求是否成功' },
      { name: 'data', type: 'array', description: '层级关系数组（从根到当前节点）' }
    ],
    requiredPermission: 'msspace:space:view (服务token权限)',
    responseExample: JSON.stringify({
      success: true,
      data: [
        {
          id: "55a8b310-f590-4671-8a78-db5a9de76e3c",
          name: "北京三元桥燕莎店",
          type: "BUILDING",
          parentId: null
        },
        {
          id: "child-id-1",
          name: "一层",
          type: "AREA",
          parentId: "55a8b310-f590-4671-8a78-db5a9de76e3c"
        }
      ],
      message: "层级路径查询成功"
    }, null, 2)
  },
  {
    id: 'service-space-building',
    method: 'GET',
    path: '/api/service/spaces/:id/building',
    name: '获取所属建筑（服务接口）',
    description: '供其他服务调用，递归向上查找空间所属的建筑节点，使用服务token验证',
    category: API_CATEGORIES.SERVICE,
    pathParams: [
      { name: 'id', type: 'string', required: true, description: '空间ID（UUID）' }
    ],
    responseFields: [
      { name: 'success', type: 'boolean', description: '请求是否成功' },
      { name: 'data', type: 'object', description: '建筑信息' }
    ],
    requiredPermission: 'msspace:space:view (服务token权限)',
    responseExample: JSON.stringify({
      success: true,
      data: {
        id: "55a8b310-f590-4671-8a78-db5a9de76e3c",
        name: "北京三元桥燕莎店",
        type: "BUILDING"
      },
      message: "所属建筑查询成功"
    }, null, 2)
  },
  {
    id: 'service-space-area',
    method: 'GET',
    path: '/api/service/spaces/:id/area',
    name: '获取所属区域（服务接口）',
    description: '供其他服务调用，递归向上查找空间所属的区域节点，使用服务token验证',
    category: API_CATEGORIES.SERVICE,
    pathParams: [
      { name: 'id', type: 'string', required: true, description: '空间ID（UUID）' }
    ],
    responseFields: [
      { name: 'success', type: 'boolean', description: '请求是否成功' },
      { name: 'data', type: 'object', description: '区域信息' }
    ],
    requiredPermission: 'msspace:space:view (服务token权限)',
    responseExample: JSON.stringify({
      success: true,
      data: {
        id: "area-id-1",
        name: "一层",
        type: "AREA"
      },
      message: "所属区域查询成功"
    }, null, 2)
  },
  {
    id: 'service-tag-list',
    method: 'GET',
    path: '/api/service/tags',
    name: '获取标签列表（服务接口）',
    description: '供其他服务调用，分页查询标签列表，使用服务token验证',
    category: API_CATEGORIES.SERVICE,
    queryParams: [
      { name: 'page', type: 'number', required: false, description: '页码', example: '1' },
      { name: 'limit', type: 'number', required: false, description: '每页条数', example: '10' },
      { name: 'search', type: 'string', required: false, description: '搜索关键词' }
    ],
    responseFields: [
      { name: 'success', type: 'boolean', description: '请求是否成功' },
      { name: 'data', type: 'array', description: '标签列表' },
      { name: 'pagination', type: 'object', description: '分页信息' }
    ],
    requiredPermission: 'msspace:tags:view (服务token权限)',
    responseExample: JSON.stringify({
      success: true,
      data: [
        {
          id: "fc6a3439-1668-44bb-b5db-4d595216c984",
          name: "建筑",
          icon: "<svg>...</svg>",
          createdAt: "2025-10-07T11:34:41.701Z",
          updatedAt: "2025-10-07T11:34:41.701Z"
        }
      ],
      pagination: {
        total: 10,
        page: 1,
        limit: 10
      },
      message: "查询成功"
    }, null, 2)
  },
  {
    id: 'service-tag-detail',
    method: 'GET',
    path: '/api/service/tags/:id',
    name: '获取标签详情（服务接口）',
    description: '供其他服务调用，根据ID获取标签详细信息，使用服务token验证',
    category: API_CATEGORIES.SERVICE,
    pathParams: [
      { name: 'id', type: 'string', required: true, description: '标签ID（UUID）' }
    ],
    responseFields: [
      { name: 'success', type: 'boolean', description: '请求是否成功' },
      { name: 'data', type: 'object', description: '标签详细信息' }
    ],
    requiredPermission: 'msspace:tags:view (服务token权限)',
    responseExample: JSON.stringify({
      success: true,
      data: {
        id: "fc6a3439-1668-44bb-b5db-4d595216c984",
        name: "建筑",
        icon: "<svg>...</svg>",
        createdAt: "2025-10-07T11:34:41.701Z",
        updatedAt: "2025-10-07T11:34:41.701Z"
      },
      message: "查询成功"
    }, null, 2)
  },
];
